<!-- src/components/security/PinDialog.svelte -->
<script>
  import { onMount, onDestroy, tick } from 'svelte';
  import { Lock, X } from 'lucide-svelte';
  import ApiClient from '$lib/api';
  import { myId } from '$lib/websocket-client.js';
  import { notify, confirm, loading } from '@components/toast';
  import { minimumDelay } from '$lib/utils';

  const api = new ApiClient();

  export let open = false;
  export let title = 'Nhập mã PIN';
  export let bypass = 0;
  export let bypassMessages = ['Nạp dữ liệu người dùng...', 'Xác thực quyền truy cập...', 'Hoàn tất...'];

  export let onSuccess = () => {};
  export let onClose = () => {};

  let PIN_LENGTH = 6;
  let MAX_ATTEMPTS = 5;
  let LOCK_SECONDS = 60;
  let hasPin = true;
  let securityLoaded = false;
  let wasOpen = false;

  let shake = false;

  let inputPin = '';
  let pinInput;

  let failedAttempts = 0;
  let lockedUntil = null;
  let lockTimer = null;
  let remainingSeconds = 0;

  let idleTimer = null;

  let focusedIndex = -1;

  let bypassRunning = false;

  $: effectiveBypass = securityLoaded && !hasPin ? normalizeBypassMs(bypass) : 0;

  $: if (open && !wasOpen) {
    wasOpen = true;
    prepareOpen();
  }

  $: if (!open && wasOpen) {
    wasOpen = false;
    securityLoaded = false;
    bypassRunning = false;
  }

  $: if (open && effectiveBypass && !bypassRunning) {
    runBypass(effectiveBypass);
  }

  $: focusedIndex = Math.min(inputPin.length, PIN_LENGTH - 1);

  $: isLocked = !!lockedUntil && Date.now() < lockedUntil;

  $: if (isLocked) {
    remainingSeconds = Math.ceil((lockedUntil - Date.now()) / 1000);
  }

  $: if (open && securityLoaded && hasPin && !bypassRunning) {
    focusPin();
  }

  function normalizeBypassMs(value) {
    const next = Number(value);

    if (!Number.isFinite(next)) return 1000;

    return Math.min(60000, Math.max(500, Math.round(next)));
  }

  async function prepareOpen() {
    await loadSecurity();
    securityLoaded = true;
  }

  async function runBypass(totalMs) {
    bypassRunning = true;

    try {

      const messages = bypassMessages || ['Nạp dữ liệu', 'Xác thực', 'Hoàn tất'];

      let remainingMs = totalMs;

      const phaseMs = Math.ceil(totalMs / messages.length);

      const updateMessage = () => {
        const phaseIndex = Math.min(messages.length - 1, Math.floor((totalMs - remainingMs) / phaseMs));

        const seconds = Math.max(1, Math.ceil(remainingMs / 1000));

        loading.update(`${messages[phaseIndex]}... ${seconds} giây`);
      };

      loading.show('');

      updateMessage();

      const timer = setInterval(() => {
        remainingMs -= 1000;

        if (remainingMs > 0) {
          updateMessage();
        }
      }, 1000);

      await minimumDelay(Promise.resolve(), totalMs);

      clearInterval(timer);

      loading.hide();

      open = false;

      onSuccess();
    } finally {
      bypassRunning = false;
    }
  }

  async function focusPin() {
    await tick();

    setTimeout(() => {
      if (!open) return;

      pinInput?.focus();

      // iOS Safari hỗ trợ tốt hơn
      try {
        pinInput?.click();
      } catch (e) {}
    }, 100);
  }

  onMount(async () => {
    await loadSecurity();
  });

  async function loadSecurity() {
    if (!$myId) {
      hasPin = false;
      return;
    }

    const rs = await api.get(`/api/user-security/${$myId}`);

    if (!rs.success) {
      return;
    }

    console.log('User security:', rs);

    hasPin = !!rs.hasPin;
    failedAttempts = rs.failedAttempts || 0;
    PIN_LENGTH = rs.pinLength || 6;
    MAX_ATTEMPTS = rs.maxAttempts || 5;
    LOCK_SECONDS = rs.lockSeconds || 60;

    if (rs.lockedUntil) {
      lockedUntil = new Date(rs.lockedUntil).getTime();
    }
  }

  function lockPin() {
    lockedUntil = Date.now() + LOCK_SECONDS * 1000;

    remainingSeconds = LOCK_SECONDS;

    lockTimer = setInterval(() => {
      remainingSeconds--;

      if (remainingSeconds <= 0) {
        clearInterval(lockTimer);

        failedAttempts = 0;
        lockedUntil = null;
        remainingSeconds = 0;

        focusPin();
      }
    }, 1000);
  }

  function close() {
    inputPin = '';
    shake = false;

    failedAttempts = 0;
    lockedUntil = null;
    remainingSeconds = 0;

    clearTimeout(idleTimer);

    if (lockTimer) {
      clearInterval(lockTimer);
      lockTimer = null;
    }

    open = false;

    onClose();
  }

  function handleInput(event) {
    if (isLocked) {
      event.target.value = inputPin;
      return;
    }

    let value = event.target.value || '';

    value = value.replace(/[^\d]/g, '');

    inputPin = value.slice(0, PIN_LENGTH);

    if (inputPin.length === PIN_LENGTH) {
      setTimeout(() => {
        verify();
      }, 100);
    }
  }

  function triggerShake() {
    navigator.vibrate?.(80);

    shake = false;

    requestAnimationFrame(() => {
      shake = true;

      setTimeout(() => {
        shake = false;

        focusPin();
      }, 400);
    });
  }

  async function verify() {
    if (isLocked) {
      return;
    }

    if (!$myId) {
      notify.error('User information is missing');
      return;
    }

    if (!hasPin) {
      notify.error('PIN has not been created');
      return;
    }

    if (inputPin.length !== PIN_LENGTH) {
      return;
    }

    try {
      const rs = await loading.run(
        async () =>
          minimumDelay(
            api.post('/api/verify-pin', {
              userId: $myId,
              pin: inputPin
            }),
            1200
          ),
        'Đang xác thực PIN...'
      );
      console.log('Verify PIN result:', rs);

      if (rs.success) {
        inputPin = '';

        failedAttempts = 0;

        open = false;

        onSuccess();

        return;
      }

      failedAttempts++;

      inputPin = '';

      if (pinInput) {
        pinInput.value = '';
      }

      triggerShake();

      if (failedAttempts >= MAX_ATTEMPTS) {
        lockPin();
      }
    } catch (err) {
      console.error(err);

      failedAttempts++;

      inputPin = '';

      if (pinInput) {
        pinInput.value = '';
      }

      triggerShake();

      if (failedAttempts >= MAX_ATTEMPTS) {
        lockPin();
      }
    }
  }

  onDestroy(() => {
    clearTimeout(idleTimer);

    if (lockTimer) {
      clearInterval(lockTimer);
    }
  });
</script>

{#if open}
  <div
    class="fixed inset-0 z-[9999]
    bg-black/20
    flex items-center justify-center">
    <div
      class="
  w-[380px]
  max-w-[95vw]
  bg-white
  border border-gray-300
  rounded-lg
  shadow-xl">
      <div
        class="h-11
        px-4
        bg-[#dbeaff]
        border-b border-gray-300
        flex items-center justify-between">
        <div class="flex items-center gap-2">
          <Lock size={16} />
          <span class="font-medium">{title}</span>
        </div>

        <button on:click={close}>
          <X size={16} />
        </button>
      </div>

      <div class="p-5">
        <div class="flex flex-col items-center">
          <div class="relative" style="width: fit-content;">
            <!-- INPUT THẬT -->

            <input
              bind:this={pinInput}
              bind:value={inputPin}
              type="tel"
              inputmode="numeric"
              pattern="[0-9]*"
              maxlength={PIN_LENGTH}
              autocomplete="one-time-code"
              autocorrect="off"
              autocapitalize="off"
              spellcheck="false"
              tabindex="0"
              class="absolute inset-0 w-full h-full opacity-0 z-20"
              style="caret-color: transparent;"
              on:input={handleInput} />

            <!-- PIN BOX DUY NHẤT -->

            <div
              class="flex gap-2"
              class:shake
              on:click={() => pinInput?.focus()}
              on:touchstart={() => pinInput?.focus()}
              on:touchend={() => pinInput?.focus()}>
              <!-- {#each Array(PIN_LENGTH) as _, i}
                <div
                  class="
        w-11 h-11
        rounded-md
        border
        flex items-center justify-center
        text-lg font-semibold
        transition-all
        {i < inputPin.length ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-300 bg-white'}">
                  {#if i < inputPin.length}
                    ●
                  {/if}
                </div>
              {/each} -->

              {#each Array(PIN_LENGTH) as _, i}
                <div
                  class="
    w-11 h-11
    rounded-md
    border
    flex items-center justify-center
    text-lg font-semibold
    transition-all

    {i < inputPin.length ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-300 bg-white'}

    {i === focusedIndex && inputPin.length < PIN_LENGTH ? '!border-blue-500 ring-1 ring-blue-300' : ''}">
                  {#if i < inputPin.length}
                    ●
                  {/if}
                </div>
              {/each}
            </div>
          </div>

          {#if failedAttempts > 0 && !isLocked}
            <div
              class="mt-3
    text-center
    text-xs
    text-amber-600">
              Sai mã PIN. Còn
              {MAX_ATTEMPTS - failedAttempts}
              lần thử.
            </div>
          {/if}

          {#if isLocked}
            <div
              class="
    mt-3
    text-center
    text-sm
    font-medium
    text-red-600">
              Đã khóa nhập PIN. Vui lòng thử lại sau
              {remainingSeconds}s
            </div>
          {/if}
        </div>
      </div>

      <div
        class="p-4
        border-t border-gray-300
        bg-gray-50
        flex justify-end gap-2">
        <button
          class="
          px-4 py-2
          border border-gray-300
          rounded-md"
          on:click={close}>
          Huỷ
        </button>

        <button
          disabled={isLocked}
          class="
          px-4 py-2
          bg-blue-600
          text-white
          rounded-md"
          on:click={verify}>
          Xác nhận
        </button>
      </div>
    </div>
  </div>
{/if}

<style>
  .shake {
    animation: shake-x 0.35s ease;
  }

  @keyframes shake-x {
    0% {
      transform: translateX(0);
    }

    15% {
      transform: translateX(-8px);
    }

    30% {
      transform: translateX(8px);
    }

    45% {
      transform: translateX(-6px);
    }

    60% {
      transform: translateX(6px);
    }

    75% {
      transform: translateX(-3px);
    }

    90% {
      transform: translateX(3px);
    }

    100% {
      transform: translateX(0);
    }
  }
</style>
